import { useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/lib/types';
import { startSessionMonitoring, stopSessionMonitoring } from '@/utils/sessionMonitor';

export const useSession = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    // Set up auth state listener FIRST (before getSession)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('Auth state changed:', event, currentSession ? 'session exists' : 'no session');
        if (!mounted) return;

        // Handle signed out event
        if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          setSession(null);
          setUser(null);
          setProfile(null);
          stopSessionMonitoring();
          return;
        }

        // Synchronous state updates only
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // IMPORTANT: Let initSession handle the first load; ignore INITIAL_SESSION here
        if (
          currentSession?.user &&
          (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')
        ) {
          // Defer any Supabase calls to avoid async inside the callback
          setTimeout(() => {
            fetchProfile(currentSession.user!.id);
            startSessionMonitoring();
          }, 0);
        }

        // DO NOT set isLoading(false) here — initSession controls initial loading state
      }
    );
    
    // THEN check for existing session
    const initSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting initial session:', error);
        }
        
        if (!mounted) return;
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          await fetchProfile(initialSession.user.id);
          
          // Start session monitoring
          startSessionMonitoring();
        }
      } catch (error) {
        console.error('Session initialization error:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    initSession();
    
    // Cleanup function
    return () => {
      mounted = false;
      subscription.unsubscribe();
      stopSessionMonitoring();
    };
  }, []); // Empty dependency array - only run once on mount

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      setProfile(data as Profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  return { session, user, profile, isLoading, setProfile };
};
